import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { IOption } from '../../../../controls/models/option';
import { CibVoteColumnComponent } from './cib-vote-column/cib-vote-column.component';
import { CibHistoryColumnComponent } from './cib-history-column/cib-history-column.component';
import { filter, from, Subject, Subscription, take } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { appConstants } from '../../../../../shared/constants/app-constants';
import { UnifiedBallotStatus } from '../../../../../shared/enums/unified-ballot-status-enum';
import { IUser } from '../../../../../shared/models/user';
import { AuthService } from '../../../../../shared/services/auth.service';
import { SamlService } from '../../../../../shared/services/saml.service';
import { UserUtility } from '../../../../../shared/utilities/user-utility';
import { ITableColumn, TableColumnType } from '../../../../controls/table/table-column';
import { IBallotCommitteeMemberDto } from '../../../shared/models/ballot-base/ballot-committee-member-dto';
import { ICommitteeInternalBallotDto } from '../../../shared/models/ci/committee-internal-ballot-dto';
import { CibPlaceVoteComponent } from '../../cib-place-vote/cib-place-vote.component';
import { Country } from '../../../../../shared/constants/srs-ballot.constants';

@Component({
    selector: 'app-cib-votes',
    templateUrl: './cib-votes.component.html',
    styleUrl: './cib-votes.component.scss',
    standalone: false
})
export class CibVotesComponent implements OnInit, OnDestroy {
  @Input() cibBallotDetail: ICommitteeInternalBallotDto | null = null;
  @Output() afterSave = new EventEmitter<string>();
  afterSaveSubject: Subject<string> = new Subject<string>();

  allVotes: IBallotCommitteeMemberDto[] = [];
  showingVotes: IBallotCommitteeMemberDto[] = [];
  selectedVotes: IBallotCommitteeMemberDto[] = [];
  votingRightDropdown: string = '';
  locationDropdown: string = '';
  isShowingLocations: boolean = true;
  isJoint: boolean = false;
  selectedVoteCount: number = 0;
  isUserPmOrAbove: boolean = false;
  isUserCM: boolean = false;
  isHoSD: boolean = false;
  isUserPmOrAboveOrHoSD: boolean = false;
  currentUser: IUser | null = null;
  currentUserCommitteeCodes: string[] = [];
  subscriptions: Subscription = new Subscription();
  loginUserEmail: string = '';
  isDropdownChanged: boolean = false;
  selectedPageNumber: number = 1;
  pageSize: number = 50;
  totalRecords: number = 0;
  eligible = appConstants.votingRights.eligible;
  notEligible = appConstants.votingRights.notEligible;

  isProxyVoteEnable: boolean = false;

  votingRightOptions: IOption<string>[] = [
    { value: 'eligible', label: this.eligible },
    { value: 'notEligible', label: this.notEligible },
  ];

  locationOptions: IOption<string>[] = [
    { value: 'aus-nz', label: 'AUS & NZ' },
    { value: 'au', label: 'AUS' },
    { value: 'nz', label: 'NZ' },
  ];



  constructor(
    public _authService: AuthService,
    private _samlService: SamlService,
    private _modalService: NgbModal,
  ) {
    this.initUser();
    this.setupAfterSaveSubjectSubscription();
  }

  initUser() {
    const currentUserSubscription =
      this._authService.currentUserSubject
      .pipe(
        filter(user => !!user),
        take(1)
      )
      .subscribe((user) => {
        this.currentUser = user;
        if (this.currentUser) {
          this.isUserPmOrAbove = UserUtility.userIsPmOrAbove(this.currentUser);
          this.isUserCM = UserUtility.isCommitteeMember(this.currentUser);
          this.loginUserEmail = this.currentUser.email;
          if(!this.isUserPmOrAbove) {
            this._samlService
            .getLoggedInUserHoSDStatus()
            .subscribe((response) => {
              this.isHoSD = response.content;
              this.isUserPmOrAboveOrHoSD = this.isUserPmOrAbove || this.isHoSD;
            });
          }else {
            this.isUserPmOrAboveOrHoSD = true
          }
          this.updateTableColumns();
        }
      });
    this.subscriptions.add(currentUserSubscription);
  }

  setupAfterSaveSubjectSubscription() {
    this.subscriptions.add(this.afterSaveSubject.subscribe((response) => {
      if(response) {
        this.afterSave.emit(response);
      }
    }));
  }


  viewVoteTableColumns: ITableColumn<IBallotCommitteeMemberDto>[] = [
    {
      name: 'checkbox',
      displayName: 'value',
      isSortable: false,
      type: TableColumnType.checkbox,
      getDisplayValue: (row: IBallotCommitteeMemberDto) => row.isEligible,
      getIsCheckBoxVisible: (row: IBallotCommitteeMemberDto) => row.isEligible,
      isHidden: this.isUserCM,
    },
    {
      name: 'fullName',
      displayName: 'Committee Member',
      isSortable: true,
      type: TableColumnType.string,
    },
    {
      name: 'organisation',
      displayName: 'Organisation',
      type: TableColumnType.string,
      isSortable: true,
    },
    {
      name: 'role',
      displayName: 'Role',
      type: TableColumnType.string,
      isSortable: true,
    },
    {
      name: 'country',
      displayName: 'Country',
      isSortable: true,
      type: TableColumnType.string,
    },
    {
      name: 'votingRights',
      displayName: 'Voting Rights',
      isSortable: true,
      type: TableColumnType.string,
    },
    {
      name: 'vote',
      displayName: 'Vote',
      type: TableColumnType.component,
      component: CibVoteColumnComponent,
      getComponentInput: (row: IBallotCommitteeMemberDto) => {
        return {
          vote: row,
          isUserPmOrAboveOrHoSD: this.isUserPmOrAboveOrHoSD,
          isUserCM: this.isUserCM,
          loginUserEmail: this.loginUserEmail,
        };
      },
      isSortable: true,
    },
    {
      name: 'lastUpdated',
      displayName: 'Last Updated',
      type: TableColumnType.component,
      component: CibHistoryColumnComponent,
      getComponentInput: (row: IBallotCommitteeMemberDto) => {
        return {
          vote: row,
          isProxyVoteEnable: this.isProxyVoteEnable,
          afterSaveSubject: this.afterSaveSubject,
         };
      },
      isSortable: true,
    },
  ];

  updateTableColumns() {
    this.viewVoteTableColumns.forEach((column) => {
      if (column.name === 'checkbox') {
        column.isHidden = !this.isProxyVoteEnable;
      }
    });
  }

  ngOnInit() {
    if (this.cibBallotDetail) {
      this.allVotes = this.cibBallotDetail.committeeMembers?.map((vote) => ({
        ...vote,
        votingRights: vote.isEligible ? this.eligible : this.notEligible,
      }));
      this.showingVotes = this.allVotes.length > 50
      ? this.allVotes.slice(0, 50)
      : this.allVotes;
      this.totalRecords = this.allVotes.length;
      this.isProxyVoteEnable = this.isUserPmOrAbove && (this.cibBallotDetail?.ballotSummary?.unifiedBallotStatusId == UnifiedBallotStatus.openForVoting || this.cibBallotDetail?.ballotSummary?.unifiedBallotStatusId == UnifiedBallotStatus.closedForVoting);
      this.updateTableColumns();
    }
    this.evaluateLocationDropdownVisibility();
  }

  onFilterChange(value: any, fromVotingRight: boolean) {
    if (fromVotingRight) {
      this.votingRightDropdown = value;

      if (this.locationDropdown) {
        // Apply both filters if locationDropdown is not null or empty
        const filteredByVotingRight = this.filterByVotingRight(value);
        this.showingVotes = this.filterByLocation(
          this.locationDropdown,
          filteredByVotingRight
        );
      } else {
        // Apply only voting rights filter
        this.showingVotes = this.filterByVotingRight(value);
      }
    } else {
      this.locationDropdown = value;

      if (this.votingRightDropdown) {
        // Apply both filters if votingRightDropdown is not null or empty
        const filteredByLocation = this.filterByLocation(value);
        this.showingVotes = this.filterByVotingRight(
          this.votingRightDropdown,
          filteredByLocation
        );
      } else {
        // Apply only location filter
        this.showingVotes = this.filterByLocation(value);
      }
    }
    this.isDropdownChanged = true;
    this.onRowsUpdated(this.showingVotes);
  }

  public filterByVotingRight(
    value: string,
    votes: IBallotCommitteeMemberDto[] = this.allVotes
  ): IBallotCommitteeMemberDto[] {
    switch (value) {
      case 'eligible':
        return votes.filter((vote) => vote.isEligible);
      case 'notEligible':
        return votes.filter((vote) => !vote.isEligible);
      default:
        return votes;
    }
  }

  public filterByLocation(
    value: string,
    votes: IBallotCommitteeMemberDto[] = this.allVotes
  ): IBallotCommitteeMemberDto[] {
    switch (value) {
      case 'aus-nz':
        return votes.filter(
          (vote) =>
            Country.Australia.includes(vote.country?.toLowerCase()) ||
            Country.NewZealand.includes(vote.country?.toLowerCase())
        );
      case 'au':
        return votes.filter((vote) =>
          Country.Australia.includes(vote.country?.toLowerCase())
        );
      case 'nz':
        return votes.filter((vote) =>
          Country.NewZealand.includes(vote.country?.toLowerCase())
        );
      default:
        return votes;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  evaluateLocationDropdownVisibility() {
    const hasNZCommitteeVotes = this.allVotes.find(
      (vote) =>
        vote.country && Country.NewZealand.includes(vote.country.toLowerCase())
    );
    this.isShowingLocations = hasNZCommitteeVotes != null && this.isJoint;
  }

  onRowsUpdated(updatedRows: IBallotCommitteeMemberDto[]): void {
    // selected votes for proxy vote
    this.showingVotes = updatedRows;
  }

  onSelectedRowsChanged(updatedRows: IBallotCommitteeMemberDto[]): void {
    // selected votes for proxy vote
    this.selectedVotes = updatedRows;
    this.selectedVoteCount = this.selectedVotes.length;
  }

  onPageChange(page: number): void {
    this.selectedPageNumber = page;
    const startIndex = (page - 1) * this.pageSize;
    const endIndex = Math.min(startIndex + this.pageSize, this.allVotes.length);
    const paginatedVotes = this.allVotes.slice(startIndex, endIndex);
    this.showingVotes = paginatedVotes;
  }
  openProxyVoteModal() {
    const modalRef = this._modalService.open(CibPlaceVoteComponent, { windowClass: 'place-vote-custom-modal', centered: true});
    modalRef.componentInstance.votes = this.selectedVotes;
    modalRef.componentInstance.isProxyVote = true;
    modalRef.result.then(
      (result) => {
        console.log('Modal closed with:', result);
      },
      vote => {
        this.afterSave.emit(this.selectedVotes[0]?.ballotId??"");
        this.selectedVotes = [];
        this.selectedVoteCount = 0;
      })
  }
}
